import {
  AlignmentType,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import type { FieldMeta, FieldOption, PrintSettings } from '../api/types';
import { formatValue } from '../utils/formatValue';
import type { PrintRow } from './printRenderer';

export interface ExportWordInput {
  fields: FieldOption[];
  fieldMetas: FieldMeta[];
  rows: PrintRow[];
  settings: PrintSettings;
  tableName: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function buildWordFileName(tableName: string, now: Date = new Date()): string {
  const stamp =
    `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}_` +
    `${pad2(now.getHours())}${pad2(now.getMinutes())}`;
  const safeName = (tableName || '导出').replace(/[\\/:*?"<>|]/g, '_').slice(0, 40);
  return `${safeName}_${stamp}.docx`;
}

function buildDocument(input: ExportWordInput): Document {
  const { fields, fieldMetas, rows, settings } = input;
  const checked = fields.filter((f) => f.checked).sort((a, b) => a.order - b.order);
  const metaById = new Map(fieldMetas.map((m) => [m.id, m]));

  const fontSizeHalfPt = Math.max(8, Math.round(settings.fontSizePx)) * 2; // docx uses half-points

  const headerCells: TableCell[] = [];
  if (settings.showIndex) {
    headerCells.push(
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: '#', bold: true, size: fontSizeHalfPt })],
          }),
        ],
      }),
    );
  }
  for (const f of checked) {
    headerCells.push(
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: f.name, bold: true, size: fontSizeHalfPt })],
          }),
        ],
      }),
    );
  }

  const dataRows = rows.map((row, i) => {
    const cells: TableCell[] = [];
    if (settings.showIndex) {
      cells.push(
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: String(i + 1), size: fontSizeHalfPt })],
            }),
          ],
        }),
      );
    }
    for (const f of checked) {
      const meta = metaById.get(f.id);
      const text = meta ? formatValue(meta, row.cells[f.id]) : '';
      cells.push(
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: text || '', size: fontSizeHalfPt })],
            }),
          ],
        }),
      );
    }
    return new TableRow({ children: cells });
  });

  const table = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [new TableRow({ tableHeader: true, children: headerCells }), ...dataRows],
  });

  const sections: { children: (Paragraph | Table)[] }[] = [
    {
      children: [
        ...(settings.title
          ? [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                heading: HeadingLevel.HEADING_1,
                children: [
                  new TextRun({ text: settings.title, bold: true, size: 36 }), // 18pt
                ],
              }),
            ]
          : []),
        table,
      ],
    },
  ];

  return new Document({
    creator: '多维表格 · 排版打印',
    title: settings.title || input.tableName,
    sections,
  });
}

export async function exportToWord(input: ExportWordInput): Promise<{ blob: Blob; fileName: string }> {
  const doc = buildDocument(input);
  const blob = await Packer.toBlob(doc);
  const fileName = buildWordFileName(input.tableName);
  return { blob, fileName };
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
